<template>
  <div class="relative">
    <div
      ref="mapContainer"
      class="map-container"
      :style="mapContainerStyle"
    ></div>
    <LayerControl v-if="mapLoaded && layerControl" :map="map" class="" />
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  defineExpose,
  defineProps,
  watch,
  computed,
} from "vue";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import LayerControl from "@/components/maps/layers/LayerControl.vue";

// Define props
const props = defineProps({
  center: {
    type: Array,
    default: () => [0, 0],
  },
  zoom: {
    type: Number,
    default: 2,
  },
  routeGeometry: {
    type: Object,
    default: null,
  },
  waypointsGeometry: {
    type: Array,
    default: () => [],
  },
  height: {
    type: String,
    default: "500px",
  },
  layerControl: {
    type: Boolean,
    default: true,
  },
  mapControl: {
    type: Boolean,
    default: true,
  },
  thematicLayers: {
    type: Boolean,
    default: true,
  },
  mapGeolocate: {
    type: Boolean,
    default: false,
  },
  userLocation: {
    type: Object,
    default: null,
  },
});

const mapContainer = ref(null);
let map = null;
const mapLoaded = ref(false);
let markers = [];
let routeLayer = null; // Store the route layer for easy removal
let waypointsLayer = null;

// Create a ref to hold the user location marker
let userLocationMarker = null;

onMounted(() => {
  map = new maplibregl.Map({
    container: mapContainer.value,
    style: "https://tiles.openfreemap.org/styles/bright",
    center: props.center,
    zoom: props.zoom,
    hash: true,
  });

  // Add navigation control (optional)
  map.addControl(
    new maplibregl.NavigationControl({
      showCompass: true,
      showZoom: props.mapControl,
      visualizePitch: true,
    }),
    "top-right",
  );

  // Add geolocate control
  if (props.mapGeolocate) {
    const geolocate = new maplibregl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
    });
    map.addControl(geolocate);
  }

  map.on("load", () => {
    if (props.thematicLayers) {
      map.addSource("municipalities", {
        type: "vector",
        tiles: [
          "https://mobility-vt.opengis.xyz/gis_municipalities/{z}/{x}/{y}",
        ],
      });

      map.addSource("bus-stop-buffers", {
        type: "vector",
        tiles: [
          "https://mobility-vt.opengis.xyz/gis_bus_stop_buffers/{z}/{x}/{y}",
        ],
      });

      map.addSource("bus-stops", {
        type: "vector",
        tiles: ["https://mobility-vt.opengis.xyz/gis_bus_stops/{z}/{x}/{y}"],
      });

      map.addLayer({
        id: "bus-stop-buffers-layer",
        type: "fill",
        source: "bus-stop-buffers",
        "source-layer": "gis_bus_stop_buffers", // This should match your table name
        paint: {
          "fill-color": "#567EDB",
          "fill-opacity": 0.1,
        },
      });

      map.addLayer({
        id: "municipalities-layer",
        type: "line",
        source: "municipalities",
        "source-layer": "gis_municipalities", // This should match your table name
        paint: {
          "line-color": "#8356DB",
          "line-width": 1,
        },
      });

      map.addLayer({
        id: "bus-stops-layer",
        type: "circle",
        source: "bus-stops",
        "source-layer": "gis_bus_stops", // This should match your table name
        paint: {
          "circle-radius": 4,
          "circle-color": "#567EDB", // Red color for bus stops
          "circle-stroke-width": 1,
          "circle-stroke-color": "#FFFFFF",
        },
      });
    }

    // Initialize route if there are initial routeGeometry
    if (props.routeGeometry) {
      displayRoute(props.routeGeometry);
    }

    // Initialize markers if there are initial waypoints
    if (props.waypointsGeometry.length > 0) {
      displayWaypoints(props.waypointsGeometry);
    }

    mapLoaded.value = true;
  });
});

onUnmounted(() => {
  if (map) {
    map.remove();
    map = null;
  }
  markers = [];
  if (userLocationMarker) {
    userLocationMarker.remove();
  }
});

// Expose methods to interact with the map
const addMarker = (lngLat, options = {}) => {
  const marker = new maplibregl.Marker(options).setLngLat(lngLat).addTo(map);
  markers.push(marker); // Store reference for removal
};

const removeMarkers = () => {
  markers.forEach((marker) => marker.remove());
  markers = []; // Clear marker references
};

const fitBounds = (layerId, options = {}) => {
  if (map.getSource(layerId)) {
    const data = map.getSource(layerId)._data;
    const bounds = new maplibregl.LngLatBounds();

    if (data.type === "Feature") {
      // For routes (LineString)
      data.geometry.coordinates.forEach((coord) => bounds.extend(coord));
    } else if (data.type === "FeatureCollection") {
      // For waypoints (Points)
      data.features.forEach((feature) =>
        bounds.extend(feature.geometry.coordinates),
      );
    }

    map.fitBounds(bounds, options);
  }
};

/*
  Watcher for dynamic map update
*/

// Watch for changes in routeGeometry
watch(
  () => props.routeGeometry,
  (newGeometry) => {
    if (map && mapLoaded.value) {
      if (newGeometry) {
        displayRoute(newGeometry);
      } else {
        clearRoute();
      }
    }
  },
  { deep: true },
);

// Watch for changes in waypointsGeometry
watch(
  () => props.waypointsGeometry,
  (newWaypoints) => {
    if (map && mapLoaded.value) {
      displayWaypoints(newWaypoints);
    }
  },
  { deep: true },
);

// Watch for changes in lastReportedLocation from the tracking store
const isInitialZoom = ref(true);

watch(
  () => props.userLocation,
  (newLocation) => {
    if (newLocation && map) {
      const { lng, lat } = newLocation;
      if (userLocationMarker) {
        userLocationMarker.setLngLat([lng, lat]);
      } else {
        const markerElement = createUserLocationElement(); // Your custom SVG element function
        userLocationMarker = new maplibregl.Marker({ element: markerElement })
          .setLngLat([lng, lat])
          .addTo(map);
      }

      // Zoom the map and set zoom only the first time
      if (isInitialZoom.value) {
        map.flyTo({ center: [lng, lat], zoom: 14 });
        isInitialZoom.value = false;
      } else {
        map.flyTo({ center: [lng, lat], zoom: map.getZoom() });
      }
    }
  },
  { immediate: true },
);

/*
  Display routes
*/

const displayRoute = (geometry) => {
  clearRoute(); // Remove any existing route

  map.addSource("route", {
    type: "geojson",
    data: {
      type: "Feature",
      geometry,
    },
  });

  map.addLayer({
    id: "route",
    type: "line",
    source: "route",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": "#0074D9",
      "line-width": 4,
    },
  });

  // Calculate and fit bounds
  if (!userLocationMarker) {
    fitBounds("route", { padding: 50, maxZoom: 14 });
  }

  routeLayer = "route"; // Store the layer ID for later removal
};

const clearRoute = () => {
  if (routeLayer) {
    map.removeLayer("route");
    map.removeSource("route");
    routeLayer = null;
  }
};

/*
  Waypoints waypoints
*/
const displayWaypoints = (waypoints) => {
  clearWaypoints();

  const geojsonData = {
    type: "FeatureCollection",
    features: waypoints.map((waypoint, index) => ({
      type: "Feature",
      properties: {
        index,
        isFirst: index === 0,
        isLast: index === waypoints.length - 1,
        isCompleted: Boolean(waypoint.time_fact),
      },
      geometry: {
        type: "Point",
        coordinates: [parseFloat(waypoint.lng), parseFloat(waypoint.lat)],
      },
    })),
  };

  map.addSource("waypoints", {
    type: "geojson",
    data: geojsonData,
  });

  map.addLayer({
    id: "waypoints",
    type: "circle",
    source: "waypoints",
    paint: {
      "circle-radius": 9,
      "circle-color": [
        "case",
        ["==", ["get", "isFirst"], true],
        "gray", // Start point
        ["==", ["get", "isLast"], true],
        "gray", // End point
        ["==", ["get", "isCompleted"], true],
        "green", // Completed points
        "red", // Not completed points
      ],
      "circle-stroke-width": 2,
      "circle-stroke-color": "#ffffff",
    },
  });

  // Add text labels for waypoints (except start and end)
  map.addLayer({
    id: "waypoint-labels",
    type: "symbol",
    source: "waypoints",
    layout: {
      "text-field": ["get", "index"],
      "text-size": 12,
      // "text-offset": [0, 0],
      "text-allow-overlap": false,
      "text-ignore-placement": false,
      visibility: "visible",
    },
    paint: {
      "text-color": "#ffffff",
      // "text-halo-color": "#ffffff",
      // "text-halo-width": 1.5,
    },
    // Filter to only show labels for waypoints that are not first or last
    filter: [
      "all",
      ["!=", ["get", "isFirst"], true],
      ["!=", ["get", "isLast"], true],
    ],
  });

  // Fit bounds to the first waypoint coordinates
  if (waypoints.length > 0) {
    fitBounds("waypoints", { padding: 50, maxZoom: 14 });
  }

  waypointsLayer = "waypoints";
};

const clearWaypoints = () => {
  if (waypointsLayer) {
    if (map.getLayer("waypoints")) map.removeLayer("waypoints");
    if (map.getLayer("waypoint-labels")) map.removeLayer("waypoint-labels");
    if (map.getSource("waypoints")) map.removeSource("waypoints");
    waypointsLayer = null;
  }
};

/*
  Map style
*/

// Create a custom marker element with an SVG icon
const createUserLocationElement = () => {
  const el = document.createElement("div");
  el.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="blue">
      <path d="M240-200v40q0 17-11.5 28.5T200-120h-40q-17 0-28.5-11.5T120-160v-320l84-240q6-18 21.5-29t34.5-11h440q19 0 34.5 11t21.5 29l84 240v320q0 17-11.5 28.5T800-120h-40q-17 0-28.5-11.5T720-160v-40H240Zm-8-360h496l-42-120H274l-42 120Zm68 240q25 0 42.5-17.5T360-380q0-25-17.5-42.5T300-440q-25 0-42.5 17.5T240-380q0 25 17.5 42.5T300-320Zm360 0q25 0 42.5-17.5T720-380q0-25-17.5-42.5T660-440q-25 0-42.5 17.5T600-380q0 25 17.5 42.5T660-320Z"/>
    </svg>
  `;
  // Set the size of the container
  el.style.width = "30px";
  el.style.height = "30px";
  // Make it a circle
  el.style.borderRadius = "50%";
  // Optional: Add a background color
  el.style.backgroundColor = "#fff";
  // Center the SVG within the container
  el.style.display = "flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  // Optional: Add a border if desired
  el.style.border = "2px solid blue";
  return el;
};

const mapContainerStyle = computed(() => {
  return {
    height: `${props.height}`,
  };
});

// Expose functions

defineExpose({
  addMarker,
  removeMarkers,
  fitBounds,
  displayRoute,
  clearRoute,
});
</script>

<style scoped>
.map-container {
  width: 100%;
}
</style>
